import { useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import LuxPlusInclusions from 'luxPlus/components/LuxPlusInclusions'
import InclusionsSection from '../FlashHotel/InclusionsSection'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import moment from 'moment'
import { getInclusionsViewableLuxPlusTier } from 'selectors/luxPlusSelectors'
import LargeContentToggle from 'components/Common/LargeContentToggle'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import TasteOfGoldTileBenefitMessage from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldTileBenefitMessage'
import { isHotel } from 'lib/offer/offerTypes'
import { getEffectiveCharterBundleRoute } from 'selectors/offerPage/offerPageSelectors'
import ExperiencesPassportOfferSummary from 'components/Experiences/ExperiencesPassport/ExperiencesPassportOfferSummary'

const INCLUSIONS_COLLAPSED_MAX_HEIGHT = 80

interface MappedProps {
  checkIn?: string
  duration?: number
  inclusionsViewableLuxPlusTier: App.MembershipSubscriptionTier
  showReturnFlightsInclusion: boolean
}

interface Props extends MappedProps {
  offer: App.HotelOffer
  packageOption: App.OfferPackageOptionView
  /** Occupancy for this room tile only — not the full multi-room party. */
  room: App.Occupants
  open?: boolean
  onToggle?: () => void
}

function MultipleRoomsInclusions(props: Props) {
  const {
    offer,
    checkIn,
    duration,
    packageOption,
    open,
    onToggle,
    showReturnFlightsInclusion,
    room,
  } = props

  const intl = useIntl()
  const pkg = packageOption.package
  const checkInMoment = useMemo(() => checkIn ? moment(checkIn) : undefined, [checkIn])
  const checkOutMoment = useMemo(() => checkInMoment?.clone().add(duration, 'days'), [checkInMoment, duration])
  const { inclusions, bonusInclusions, luxPlusInclusions } = useOfferInclusions(pkg, { checkIn: checkInMoment, checkOut: checkOutMoment })
  const currency = useAppSelector(state => state.geo.currentCurrency)
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)
  const showTasteOfGoldBenefit = useMemo(() => {
    if (!isTasteOfGoldActive || !isHotel(offer)) return false
    return isTasteOfGoldEligibleOffer(
      offer,
      currency,
      offer.property?.geoData?.countryCode?.toLowerCase() !== 'au',
      pkg.price,
    )
  }, [isTasteOfGoldActive, offer, currency, pkg.price])

  return (
    <LargeContentToggle
      maxHeight={INCLUSIONS_COLLAPSED_MAX_HEIGHT}
      showMoreText={intl.formatMessage({
        id: 'coqaxU_60uvmtbpTkIzd',
        defaultMessage: 'View more inclusions',
      })}
      showLessText={intl.formatMessage({
        id: 'XS80QHxOUIfnfvwqd5oI',
        defaultMessage: 'View less inclusions',
      })}
      open={open}
      onToggle={onToggle}
    >
      <VerticalSpacer gap={8}>
        {!!luxPlusInclusions.length && (
          <LuxPlusInclusions
            luxPlusInclusions={luxPlusInclusions}
            accommodationOffer={offer}
            hideUpsellModal
          />
        )}
        {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
        <InclusionsSection
          offer={offer}
          inclusions={inclusions}
          bonusInclusions={bonusInclusions}
          showReturnFlightsInclusion={showReturnFlightsInclusion}
          rooms={[room]}
        />
        <ExperiencesPassportOfferSummary />
      </VerticalSpacer>
    </LargeContentToggle>
  )
}

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  checkIn: state.checkInDate,
  duration: state.duration,
  inclusionsViewableLuxPlusTier: getInclusionsViewableLuxPlusTier(appState),
  showReturnFlightsInclusion: !!state.flights && !getEffectiveCharterBundleRoute(state) && !state.offer?.bundledWithFlightsOnly,
}), MultipleRoomsInclusions)
